import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Handshake } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function SponsorsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sponsors`);
      console.log('Sponsors loaded:', data);
      setSponsors(data);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Always render the section, even if no sponsors yet
  return (
    <section 
      id="patrocinadores"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950 border-t border-white/5"
      ref={ref}
      data-testid="sponsors-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">NUESTROS ALIADOS</span>
          <h2 
            className="text-3xl sm:text-4xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="sponsors-title"
          >
            PATROCINADORES
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Agradecemos a las empresas e instituciones que apoyan el desarrollo deportivo juvenil en Sesquilé.
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center text-zinc-500 py-8">Cargando patrocinadores...</div>
        ) : sponsors.length === 0 ? (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-20 h-20 mx-auto bg-zinc-900 rounded-full flex items-center justify-center mb-4">
              <Handshake className="text-zinc-600" size={40} />
            </div>
            <p className="text-zinc-500">¿Quieres ser patrocinador de Jotuns Basketball Club?</p>
            <a 
              href="https://wa.me/573114540684?text=Hola,%20me%20interesa%20patrocinar%20a%20Jotuns%20Basketball%20Club"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              Contáctanos
            </a>
          </motion.div>
        ) : (
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {sponsors.map((sponsor, index) => (
              <motion.a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target={sponsor.website ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="group"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                data-testid={`sponsor-${sponsor.id}`}
              >
                <div className="bg-white rounded-xl p-6 h-32 flex items-center justify-center hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-300 border-2 border-transparent hover:border-orange-500">
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150x80?text=' + encodeURIComponent(sponsor.name);
                    }}
                  />
                </div>
                <p className="text-center text-zinc-400 text-sm mt-3 group-hover:text-orange-500 transition-colors">
                  {sponsor.name}
                </p>
              </motion.a>
            ))}
          </motion.div>
        )}

        {/* Call to action for more sponsors */}
        {sponsors.length > 0 && (
          <motion.div
            className="text-center mt-12 pt-8 border-t border-white/5"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-zinc-500 text-sm">
              ¿Tu empresa quiere apoyar el deporte juvenil?{' '}
              <a 
                href="https://wa.me/573114540684?text=Hola,%20me%20interesa%20patrocinar%20a%20Jotuns%20Basketball%20Club"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-400 underline"
              >
                Conviértete en patrocinador
              </a>
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
